import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { DateInput, FormField, Input, Modal } from "ui";

import {
  CompanyCommunicationLog,
  useCreateCompanyLog,
  useUpdateCompanyLog
} from "../../../apis";

import styles from "./CommunicationLogModal.module.scss";

const FORM_ID = "communication-log-form";

interface FormValues {
  title: string;
  date: Date | null;
}

interface CommunicationLogModalProps {
  isOpen: boolean;
  companyId: string;
  log?: CompanyCommunicationLog | null;
  onClose: () => void;
}

const toIsoDate = (d: Date) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const CommunicationLogModal = ({
  isOpen,
  companyId,
  log,
  onClose
}: CommunicationLogModalProps) => {
  const { t } = useTranslation();

  const isEdit = !!log;

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<FormValues>({
    defaultValues: { title: "", date: new Date() }
  });

  useEffect(() => {
    if (!isOpen) return;
    reset({
      title: log?.title ?? "",
      date: log?.date ? new Date(log.date) : new Date()
    });
  }, [isOpen, log, reset]);

  const { mutate: createLog, isLoading: isCreating } = useCreateCompanyLog(
    companyId,
    {
      onSuccess: () => {
        toast.success(t("Entry added"));
        onClose();
      },
      onError: (e) => toast.error(e?.message || t("Failed to add"))
    }
  );

  const { mutate: updateLog, isLoading: isUpdating } = useUpdateCompanyLog(
    companyId,
    {
      onSuccess: () => {
        toast.success(t("Entry updated"));
        onClose();
      },
      onError: (e) => toast.error(e?.message || t("Failed to update"))
    }
  );

  const onSubmit = (values: FormValues) => {
    if (!values.date) return;
    const body = { title: values.title, date: toIsoDate(values.date) };
    if (isEdit && log) {
      updateLog({ id: log.id, body });
    } else {
      createLog(body);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("Communication log")}
      formId={FORM_ID}
      okText={isEdit ? t("Save changes") : t("Log entry")}
      cancelText={t("Cancel")}
      isLoading={isCreating || isUpdating}
      className={styles.modal}
    >
      <form
        id={FORM_ID}
        className={styles.form}
        onSubmit={handleSubmit(onSubmit)}
      >
        <FormField
          label={t("Title / Summary")}
          errorMessage={errors.title?.message}
        >
          <Input
            {...register("title", { required: t("Required field") })}
            placeholder={t("e.g. Quarterly business review")}
          />
        </FormField>

        <Controller
          control={control}
          name="date"
          rules={{ required: t("Required field") }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <DateInput
              value={value}
              onChange={onChange}
              label={t("Date")}
              errorMessage={error?.message}
            />
          )}
        />
      </form>
    </Modal>
  );
};

export default CommunicationLogModal;
